import * as React from "react";
import {KeyboardEvent, ReactNode} from "react";
import {alpha, Box, Checkbox} from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {useTheme} from "@mui/material/styles";

interface IProps {
    selected: boolean
    onChange: (selected: boolean) => void
    icon: ReactNode
    title: ReactNode
    description: ReactNode
    children?: ReactNode
}

const CheckoutOptionCard = (props: IProps) => {
    const theme = useTheme()

    const handleToggle = () => {
        props.onChange(!props.selected)
    }

    const handleToggleByKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            handleToggle()
        }
    }

    return (
        <Box
            p={{xs: 1.5, sm: 2}}
            borderRadius={1}
            role="checkbox"
            aria-checked={props.selected}
            tabIndex={0}
            onClick={handleToggle}
            onKeyDown={handleToggleByKeyboard}
            sx={{
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: `1px dashed ${alpha(theme.palette.primary.main, props.selected ? 0.38 : 0.22)}`,
                backgroundColor: props.selected
                    ? alpha(theme.palette.primary.main, 0.04)
                    : alpha(theme.palette.primary.main, 0.02)
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: {xs: 1.1, sm: 1.35}
                }}
            >
                <Box
                    sx={{
                        width: {xs: 38, sm: 42},
                        height: {xs: 38, sm: 42},
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, props.selected ? 0.12 : 0.08)
                    }}
                >
                    {props.icon}
                </Box>

                <Grid container alignItems="flex-start" wrap="nowrap" columnGap={1}>
                    <Box flex={1} minWidth={0}>
                        <Typography
                            variant="body1"
                            color="primary"
                            fontWeight={700}
                            sx={{
                                fontSize: {xs: "1rem", sm: "1.08rem"},
                                lineHeight: 1.2
                            }}
                        >
                            {props.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                mt: 0.45,
                                color: alpha(theme.palette.primary.main, 0.76),
                                lineHeight: 1.35
                            }}
                        >
                            {props.description}
                        </Typography>
                    </Box>
                    <Checkbox
                        sx={{color: theme.palette.primary.main, p: 0.35, pointerEvents: "none"}}
                        checked={props.selected}
                        color="primary"
                        onChange={(_, checked) => props.onChange(checked)}
                        tabIndex={-1}
                    />
                </Grid>
            </Box>

            {props.children && (
                <Box
                    mt={2}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    {props.children}
                </Box>
            )}
        </Box>
    )
}

export default CheckoutOptionCard
